import { Link } from 'react-router';
import { canReport } from '../lib/contact';
import { InfoPage, InfoSection } from './InfoPage';

/**
 * How members are expected to behave.
 *
 * Deliberately short. Every rule here is one the product can actually observe
 * or that a review can record — a guideline nobody can act on is decoration.
 */
export function GuidelinesScreen() {
  return (
    <InfoPage
      title="Community guidelines"
      standfirst="OpenRoad works because people turn up, say what they mean, and treat a stranger's car like a favour rather than a taxi. That is most of it. The rest is below."
    >
      <InfoSection heading="Turn up, or say you cannot">
        <p className="m-0">
          A seat you booked and did not take is a seat somebody else could have had, and a driver
          who waited. Plans change — cancel as soon as you know, not at the roadside.
        </p>
        <p className="m-0">
          The same runs the other way. A driver who cancels a ride leaves passengers without a way
          home, sometimes in a town they do not live in.
        </p>
      </InfoSection>

      <InfoSection heading="Say what the ride is">
        <p className="m-0">
          Set the pickup point somewhere a passenger can actually find, put the real departure time
          in, and describe the ride as it will be. If there is room for one bag and not three, write
          that down.
        </p>
        <p className="m-0">
          Price the seat for the trip, not for the passenger's need. Prices include the platform
          fee, so what you set is what somebody pays.
        </p>
      </InfoSection>

      <InfoSection heading="The car is somebody's">
        <p className="m-0">
          Whoever is driving decides about smoking, pets, music and how much talking there will be —
          and every ride says so before you book. A ride that says no pets means no pets, not a
          negotiation at the door.
        </p>
        <p className="m-0">Wear the seatbelt. Do not distract the person driving.</p>
      </InfoSection>

      <InfoSection heading="Reviews are for what happened">
        <p className="m-0">
          Write about the journey — punctuality, the driving, the company. Not about somebody's
          accent, their politics, or a fare you agreed to and later regretted.
        </p>
        <p className="m-0">
          Reviews are only possible after a ride has actually happened, and both sides can write
          one.
        </p>
      </InfoSection>

      <InfoSection heading="Not welcome here">
        <ul className="m-0 flex list-disc flex-col gap-1.5 pl-5">
          <li>
            Harassment or discrimination of any kind — including on race, gender, disability,
            religion or sexuality.
          </li>
          <li>Driving impaired, or riding with somebody who is.</li>
          <li>
            Running a commercial passenger service through OpenRoad. This is cost-sharing between
            people going the same way.
          </li>
          <li>Asking to be paid outside the app, or asking somebody else to.</li>
          <li>Accounts that are not who they say they are.</li>
        </ul>
        {canReport ? (
          <p className="m-0">
            If you see any of it, say so — the <Link to="/contact">contact page</Link> has the
            address.
          </p>
        ) : (
          <p className="m-0">
            There is currently no way to report any of it to us — see the{' '}
            <Link to="/contact">contact page</Link>, which says so plainly rather than sending you
            in a circle. Until there is, the guidelines above are what the community holds itself to
            rather than something anybody enforces.
          </p>
        )}
      </InfoSection>
    </InfoPage>
  );
}
